import { useState } from "react";
import { useLanguage } from "@/context/LanguageContext";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Building2, User, Mail, Lock, ArrowRight, AlertCircle, Loader2, CheckCircle } from "lucide-react";
import { authApi, tokenStore } from "@/lib/api";
import { usersApi } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const LOGO_URL = "https://assets-h2.izmocars.com/dealerlogos/qcoresecure.com.png";

function PasswordStrength({ password }) {
  const checks = [
    { label: "12+ characters", ok: password.length >= 12 },
    { label: "Uppercase", ok: /[A-Z]/.test(password) },
    { label: "Lowercase", ok: /[a-z]/.test(password) },
    { label: "Number", ok: /\d/.test(password) },
    { label: "Special char", ok: /[!@#$%^&*()_+\-=\[\]{}|;':",./<>?]/.test(password) },
  ];
  const score = checks.filter(c => c.ok).length;
  const colors = ["bg-red-400", "bg-red-400", "bg-orange-400", "bg-yellow-400", "bg-cyan-500"];
  return (
    <div className="mt-2 space-y-1">
      <div className="flex gap-1">
        {[1,2,3,4,5].map(i => (
          <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i <= score ? colors[score - 1] : "bg-slate-200"}`} />
        ))}
      </div>
      <div className="flex flex-wrap gap-x-3 gap-y-0.5">
        {checks.map(c => (
          <span key={c.label} className={`text-[10px] ${c.ok ? "text-cyan-600" : "text-slate-400"}`}>
            {c.ok ? "✓" : "○"} {c.label}
          </span>
        ))}
      </div>
    </div>
  );
}

export default function RegisterPage() {
  const { t } = useLanguage();
  const [form, setForm] = useState({
    org: "", first: "", last: "", email: "", password: "",
  });
  const [agreed, setAgreed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { completeLogin } = useAuth();
  const navigate = useNavigate();

  const update = (k) => (e) => setForm(prev => ({ ...prev, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    // Manual translated validation
    if (!form.org.trim()) { setError("Organization name is required"); return; }
    if (!form.first.trim()) { setError("First name is required"); return; }
    if (!form.last.trim()) { setError("Last name is required"); return; }
    if (!form.email) { setError("Email is required"); return; }
    if (!form.email.includes("@")) { setError("Please enter a valid email address"); return; }
    if (!form.password) { setError("Password is required"); return; }
    if (!agreed) { setError("Please accept the Terms of Service"); return; }
    setError("");
    setLoading(true);
    try {
      await authApi.register({
        email: form.email,
        password: form.password,
        first_name: form.first,
        last_name: form.last,
        organization: form.org,
      });
      // Auto-login after registration
      const loginData = await authApi.login({ email: form.email, password: form.password });
      if (!loginData.requires_2fa) {
        tokenStore.setAccess(loginData.access_token);
        tokenStore.setRefresh(loginData.refresh_token);
        completeLogin(loginData);
        toast.success("Account created! Welcome to QCoreSecure.");
        navigate("/dashboard");
      } else {
        navigate("/login");
      }
    } catch (err) {
      setError(err.message || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="register-page">
      {/* Left Panel */}
      <div className="hidden lg:flex lg:w-1/2 bg-[#020617] relative overflow-hidden flex-col justify-between p-12">
        <div className="absolute inset-0 bg-grid-pattern opacity-30" />
        <div className="absolute top-0 left-0 w-96 h-96 bg-cyan-500/5 rounded-full blur-3xl" />
        <div className="relative z-10">
          <Link to="/" className="flex items-center gap-3 mb-20" data-testid="register-logo">
            <img src={LOGO_URL} alt="QCoreSecure" className="h-8 object-contain" />
          </Link>
          <h2 className="text-4xl font-bold text-white tracking-tight leading-tight mb-4">
            Start Building with<br />QCoreSecure QRNG
          </h2>
          <p className="text-slate-400 max-w-md leading-relaxed mb-12">
            Get your API keys in minutes. No credit card required for the developer tier.
          </p>
          <div className="space-y-4">
            {[
              "10,000 free API calls per month",
              "Full API documentation & SDKs",
              "Real-time entropy health monitoring",
              "NIST SP 800-90A/B compliant output",
            ].map((item, i) => (
              <div key={i} className="flex items-center gap-3 text-sm text-slate-300">
                <div className="w-5 h-5 border border-cyan-500/30 bg-cyan-500/5 flex items-center justify-center flex-shrink-0">
                  <span className="text-[10px] text-cyan-400 font-bold">{i + 1}</span>
                </div>
                {item}
              </div>
            ))}
          </div>
        </div>
        <div className="relative z-10 text-xs text-slate-600">
          Trusted by enterprises in BFSI, Defense, Gaming, and Web3 &mdash; QCoreSecure, San Francisco, CA
        </div>
      </div>

      {/* Right Panel */}
      <div className="flex-1 flex items-center justify-center bg-[#F8FAFC] p-8">
        <div className="w-full max-w-md">
          <div className="lg:hidden flex items-center gap-3 mb-12">
            <Link to="/" ><img src={LOGO_URL} alt="QCoreSecure" className="h-7 object-contain" /></Link>
          </div>

          <h1 className="text-2xl font-bold text-slate-900 tracking-tight mb-2" data-testid="register-title">{t('register.title')}</h1>
          <p className="text-sm text-slate-500 mb-8">
            Already have an account?{" "}
            <Link to="/login" className="text-cyan-600 hover:text-cyan-700 font-medium" data-testid="register-login-link">Sign in</Link>
          </p>

          {error && (
            <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded text-sm text-red-700" data-testid="register-error">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <form className="space-y-5" onSubmit={handleSubmit} noValidate data-testid="register-form">
            <div>
              <Label htmlFor="org" className="text-xs uppercase tracking-wider text-slate-500 font-medium">Organization Name</Label>
              <div className="relative mt-1.5">
                <Building2 className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input id="org" value={form.org} onChange={update("org")} placeholder="Acme Corporation"
                  className="pl-10 h-11 bg-white border-slate-200" data-testid="register-org-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="first" className="text-xs uppercase tracking-wider text-slate-500 font-medium">{t('register.first_name')}</Label>
                <div className="relative mt-1.5">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input id="first" value={form.first} onChange={update("first")} placeholder="Jane"
                    className="pl-10 h-11 bg-white border-slate-200" data-testid="register-first-input" />
                </div>
              </div>
              <div>
                <Label htmlFor="last" className="text-xs uppercase tracking-wider text-slate-500 font-medium">Last Name</Label>
                <Input id="last" value={form.last} onChange={update("last")} placeholder="Doe"
                  className="h-11 bg-white border-slate-200 mt-1.5" data-testid="register-last-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="reg-email" className="text-xs uppercase tracking-wider text-slate-500 font-medium">Work Email</Label>
              <div className="relative mt-1.5">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input id="reg-email" type="email" value={form.email} onChange={update("email")} placeholder="jane@company.com"
                  className="pl-10 h-11 bg-white border-slate-200" data-testid="register-email-input" />
              </div>
            </div>
            <div>
              <Label htmlFor="reg-password" className="text-xs uppercase tracking-wider text-slate-500 font-medium">Password</Label>
              <div className="relative mt-1.5">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input id="reg-password" type="password" value={form.password} onChange={update("password")} placeholder="Min. 12 characters"
                  className="pl-10 h-11 bg-white border-slate-200" data-testid="register-password-input" />
              </div>
              {form.password && <PasswordStrength password={form.password} />}
            </div>
            <div className="flex items-start gap-2">
              <Checkbox id="terms" checked={agreed} onCheckedChange={setAgreed} className="mt-0.5" data-testid="register-terms-checkbox" />
              <Label htmlFor="terms" className="text-sm text-slate-600 cursor-pointer leading-relaxed">
                I agree to the <a href="#" className="text-cyan-600 hover:underline">Terms of Service</a> and <a href="#" className="text-cyan-600 hover:underline">Privacy Policy</a>
              </Label>
            </div>
            <button type="submit" disabled={loading}
              className="w-full h-11 bg-slate-900 text-white hover:bg-slate-800 transition-colors uppercase tracking-widest text-sm font-bold flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="register-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <>{t('register.create_account')} <ArrowRight className="w-4 h-4" /></>}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
}
